import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import { useUser } from '../../user/UserContext';
import useApi from '../../api';
import { formatCalendarOptionLabel } from '../../../lib/calendarIntegrationLabel';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowDown, ArrowUp, Crown, Loader2, Lock, Pencil, Plus, Trash2, Zap, Link as LinkIcon, Calendar, User, Info, Sparkles, Scale, X, CheckCircle2, Circle, Database, Route, Play, Target, Navigation, ExternalLink, MessageSquare, Tag } from 'lucide-react';
import Upgrade from '../../modals/Upgrade';
import ConditionBuilder from './ConditionBuilder';
import { validateFormula } from './formulaValidator';
import { buildWorkflowConditionFields } from './workflowConditionFields';

import '@xyflow/react/dist/style.css';
import {
  BaseEdge,
  EdgeLabelRenderer,
  ReactFlow,
  Background,
  Controls,
  Handle,
  Position,
  getSmoothStepPath,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import * as dagre from 'dagre';

import { VisitorTrackNode, RoutingCondition, TrackClassificationRules, TrackTerminalAction } from '../tabs/types';

type TaxonomyField = {
  name: string;
  label: string;
  required: boolean;
  question: boolean;
  ai_label?: boolean;
  description?: string;
  type?: string;
  options?: string[];
  hidden?: boolean;
};

interface TrackBuilderProps {
  agentId?: string;
  agentGoal?: string;
  agentPersona?: string;
  trackNodes: VisitorTrackNode[];
  setTrackNodes: (nodes: VisitorTrackNode[]) => void;
  enableTracks: boolean;
  setEnableTracks: (value: boolean) => void;
  setNeedsUpdate: (value: boolean) => void;
  form: any[];
  actions: Record<string, any>;
}

type TrackFlowNodeData = {
  node: VisitorTrackNode;
  isBusiness: boolean;
  isRoot: boolean;
  directFieldCount: number;
  effectiveFieldCount: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** True when this node is the auto-created locked Onboarding child. */
  isOnboardingNode: boolean;
  /** True when this node has a start_onboarding terminal action (hide Add-child). */
  hasStartOnboardingAction: boolean;
  /** Form field definitions for resolving field names to labels. */
  formFields: TaxonomyField[];
  onAddChild: (parentSystemName: string) => void;
  onEdit: (node: VisitorTrackNode) => void;
  onEditAction: (node: VisitorTrackNode) => void;
  onDelete: (node: VisitorTrackNode) => void;
  onMove: (node: VisitorTrackNode, direction: 'up' | 'down') => void;
};

type TrackDiagramNode = Node<TrackFlowNodeData, 'trackNode'>;

type TrackEdgeClassification = 'rules' | 'ai' | 'none';
type TrackEdgeRuleCondition = {
  field: string;
  operator: string;
  value: string;
};
type TrackEdgeData = {
  classification: TrackEdgeClassification;
  targetName: string;
  /** Condensed human-readable summary for AI classification. */
  classificationSummary?: string;
  /** Structured first rule condition for rules classification. */
  ruleCondition?: TrackEdgeRuleCondition;
  /** Number of additional conditions beyond the first. */
  extraConditionCount?: number;
  /** Callback to open the target node's editor. */
  onEditTarget?: () => void;
};
type TrackDiagramEdge = Edge<TrackEdgeData, 'classificationEdge'>;

const REQUIRED_SCHEDULING_TRACK_FIELDS = ['email'];

const getSchedulingMappedFields = (schedulingFields: Record<string, unknown>) => {
  const mappedFieldKeys = Object.entries(schedulingFields)
    .filter(
      ([fieldName, targetFieldId]) =>
        typeof fieldName === 'string' &&
        fieldName.trim().length > 0 &&
        String(targetFieldId || '').trim().length > 0
    )
    .map(([fieldName]) => fieldName);

  const requiredTrackFields = [...REQUIRED_SCHEDULING_TRACK_FIELDS];

  return { mappedFieldKeys, requiredTrackFields };
};

function TrackClassificationEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}: EdgeProps<TrackDiagramEdge>) {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Horizontal: align with target node's X so sibling labels don't overlap.
  // Vertical: midpoint between source and target (where the original label sat).
  const anchorX = targetX;
  const anchorY = (sourceY + targetY) / 2;

  const classification = data?.classification ?? 'none';
  const targetName = data?.targetName || 'this sub-track';
  const summary = data?.classificationSummary || '';
  const ruleCondition = data?.ruleCondition;
  const extraConditionCount = data?.extraConditionCount || 0;
  const onEditTarget = data?.onEditTarget;

  const operatorSymbol: Record<string, string> = {
    equals: '=',
    contains: 'contains',
    less_than: '<',
    greater_than: '>',
    in: 'in',
  };

  const hasConfigured = (classification === 'rules' && ruleCondition) || (classification === 'ai' && summary);

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${anchorX}px,${anchorY}px)`,
            pointerEvents: 'all',
          }}
        >
          {hasConfigured ? (
            // Configured edge: show rule/AI card with edit icon on hover
            <div className="group/edge relative">
              {classification === 'rules' && ruleCondition ? (
                <div
                  className="flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-md shadow-md cursor-pointer border border-border bg-background border-l-[3px] border-l-emerald-500"
                  onClick={onEditTarget}
                >
                  <span className="text-[10px] font-medium text-foreground truncate max-w-[140px]">
                    {ruleCondition.field}
                  </span>
                  <span className="text-[9px] font-mono text-muted-foreground">
                    {operatorSymbol[ruleCondition.operator] || ruleCondition.operator}
                  </span>
                  <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 truncate max-w-[140px]">
                    {ruleCondition.value}
                  </span>
                  {extraConditionCount > 0 && (
                    <span className="text-[9px] text-muted-foreground">
                      +{extraConditionCount} more
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="max-w-[200px] flex items-start gap-1.5 px-2 py-1.5 rounded-md shadow-md cursor-pointer border-l-[3px] bg-background text-foreground border border-border border-l-purple-500"
                  onClick={onEditTarget}
                >
                  <Sparkles className="w-3 h-3 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span className="text-[10px] leading-tight line-clamp-2">
                    {summary}
                  </span>
                </div>
              )}
              {/* Edit icon on hover */}
              <div
                className="absolute -top-2 -right-2 hidden group-hover/edge:flex w-5 h-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md cursor-pointer"
                onClick={onEditTarget}
              >
                <Pencil className="w-2.5 h-2.5" />
              </div>
            </div>
          ) : (
            // Empty edge: show placeholder to configure
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-sm cursor-pointer border border-dashed border-border bg-background hover:border-primary/50 hover:bg-muted/50 transition-colors"
                    onClick={onEditTarget}
                  >
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">
                      Add condition
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  Configure how visitors are routed to {targetName}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

function truncateText(text: string, maxLen: number): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  return trimmed.slice(0, maxLen).trimEnd() + '…';
}


function getTerminalActionInfo(action?: TrackTerminalAction): { icon: typeof Calendar; label: string } | null {
  if (!action) return null;
  switch (action.type) {
    case 'calendar':
      return {
        icon: Calendar,
        label: action.calendar_config?.name
          ? `Book: ${truncateText(formatCalendarOptionLabel({
              providerName: action.calendar_config.name,
              details: action.calendar_config.details,
            }), 25)}`
          : 'Book Meeting',
      };
    case 'link':
      return { icon: ExternalLink, label: truncateText(action.value || 'Open Link', 30) };
    case 'navigate':
      return { icon: Navigation, label: truncateText(action.value || 'Navigate', 30) };
    case 'escalation':
      return { icon: MessageSquare, label: 'Escalate' };
    case 'start_onboarding':
      return { icon: Play, label: 'Start Onboarding' };
    default:
      return null;
  }
}

function TrackFlowNode({ data }: NodeProps<TrackDiagramNode>) {
  const {
    node,
    isBusiness,
    isRoot,
    directFieldCount,
    effectiveFieldCount,
    canMoveUp,
    canMoveDown,
    isOnboardingNode,
    hasStartOnboardingAction,
    formFields,
    onAddChild,
    onEdit,
    onEditAction,
    onDelete,
    onMove,
  } = data;

  const hideMove = false;
  const hideAddChild = false;
  const hideDelete = false;

  // Resolve field names to labels
  const fieldLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of formFields) {
      map.set(f.name, f.label || f.name);
    }
    return map;
  }, [formFields]);

  const goalText = !isRoot ? truncateText(node.conversation_goals || '', 80) : '';
  const terminalAction = getTerminalActionInfo(node.terminal_action);

  const allFields = [
    ...(node.fields_to_collect || []).map((name) => ({ name, label: fieldLabelMap.get(name) || name, isLabel: false })),
    ...(node.labels_to_collect || []).map((name) => ({ name, label: fieldLabelMap.get(name) || name, isLabel: true })),
  ];
  const maxVisibleFields = 3;
  const visibleFields = allFields.slice(0, maxVisibleFields);
  const extraFieldCount = allFields.length - maxVisibleFields;

  return (
    <div
      className={clsx(
        'group relative w-[240px] rounded-lg border bg-card shadow-sm transition-colors hover:border-foreground/25',
        isRoot
          ? 'border-primary/30 bg-primary/[0.02]'
          : isOnboardingNode
            ? 'border-foreground/15 bg-muted/20'
            : 'border-border',
        !isBusiness && 'opacity-70'
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-primary/60 !w-3 !h-3 !-top-1.5" />

      <div className="flex flex-col gap-1.5 p-3">
        {/* Header: Icon + Name + Badge */}
        <div className="flex items-center gap-1.5">
          <div
            className={clsx(
              'w-6 h-6 rounded-md flex-shrink-0 flex items-center justify-center',
              isRoot
                ? 'bg-primary/10 text-primary'
                : isOnboardingNode
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {isRoot ? (
              <Crown className="w-3.5 h-3.5" />
            ) : isOnboardingNode ? (
              <Play className="w-3.5 h-3.5" />
            ) : (
              <div className="w-2 h-2 rounded-full bg-current opacity-60" />
            )}
          </div>
          <div className="font-semibold text-xs leading-tight line-clamp-1 flex-1 min-w-0">
            {node.name || node.system_name}
          </div>
          {isRoot && (
            <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 bg-primary/10 text-primary border-0 flex-shrink-0">
              Root
            </Badge>
          )}
          {isOnboardingNode && (
            <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 flex-shrink-0">
              Onboarding
            </Badge>
          )}
        </div>

        {/* Conversation goal */}
        {goalText && (
          <div className="flex items-start gap-1 px-1.5 py-1 rounded-md bg-muted/40 border border-border/30">
            <Target className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
            <span className="text-[10px] leading-tight text-muted-foreground line-clamp-2">
              {goalText}
            </span>
          </div>
        )}

        {/* Fields to collect */}
        {allFields.length > 0 && (
          <div className="px-1.5 py-1 rounded-md bg-muted/40 border border-border/30">
            <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
              Collects
            </div>
            <div className="flex flex-wrap gap-0.5">
              {visibleFields.map((f) => (
                <Badge
                  key={f.name}
                  variant="outline"
                  className={clsx(
                    'text-[9px] px-1.5 py-0 h-4 font-normal',
                    f.isLabel
                      ? 'bg-muted text-foreground border-border'
                      : 'bg-background text-foreground border-border'
                  )}
                >
                  {f.isLabel && <Tag className="w-2 h-2 mr-0.5" />}
                  {truncateText(f.label, 14)}
                </Badge>
              ))}
              {extraFieldCount > 0 && (
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 font-normal bg-background text-muted-foreground border-border">
                  +{extraFieldCount}
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Field count summary */}
        {!isRoot && directFieldCount === 0 && allFields.length === 0 && (
          <div className="text-[10px] text-muted-foreground/60 text-center">
            No fields configured
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-0.5 pt-0.5 border-t border-border/20">
          {!hideMove && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-md hover:bg-muted/80"
                onClick={isBusiness ? () => onMove(node, 'up') : undefined}
                disabled={!isBusiness || !canMoveUp}
                title="Move up"
              >
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-md hover:bg-muted/80"
                onClick={isBusiness ? () => onMove(node, 'down') : undefined}
                disabled={!isBusiness || !canMoveDown}
                title="Move down"
              >
                <ArrowDown className="h-3 w-3" />
              </Button>
            </>
          )}
          {!hideAddChild && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-md hover:bg-primary/10 hover:text-primary"
              onClick={isBusiness ? () => onAddChild(node.system_name) : undefined}
              disabled={!isBusiness}
              title="Add sub-track"
            >
              {isBusiness ? <Plus className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-md hover:bg-primary/10 hover:text-primary"
            onClick={isBusiness ? () => onEdit(node) : undefined}
            disabled={!isBusiness}
            title="Edit track"
          >
            {isBusiness ? <Pencil className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
          </Button>
          {!hideDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-md hover:bg-destructive/10 hover:text-destructive"
              onClick={isBusiness ? () => onDelete(node) : undefined}
              disabled={!isBusiness || isRoot}
              title="Delete track"
            >
              {isBusiness ? <Trash2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-primary/60 !w-3 !h-3 !-bottom-1.5" />

      {/* Terminal action card floating below the node */}
      {terminalAction && (
        <div
          className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center"
          style={{ top: '100%', paddingTop: 2 }}
        >
          {/* Connector line matching ReactFlow edge style */}
          <svg width="2" height="32" className="flex-shrink-0">
            <line x1="1" y1="0" x2="1" y2="32" stroke="var(--xy-edge-stroke, #b1b1b7)" strokeWidth="1" />
          </svg>
          <div
            className="group/action relative flex flex-col gap-1 px-2.5 py-2 rounded-md shadow-md border border-border bg-background cursor-pointer border-t-[3px] border-t-primary w-[200px]"
            onClick={() => onEditAction(node)}
          >
            <div className="flex items-center gap-1.5">
              <terminalAction.icon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              <span className="text-[10px] font-semibold text-foreground truncate">
                {terminalAction.label}
              </span>
            </div>
            {node.terminal_action_prompt && (
              <span className="text-[9px] leading-tight text-muted-foreground line-clamp-2">
                {truncateText(node.terminal_action_prompt, 80)}
              </span>
            )}
            {node.terminal_action?.type === 'calendar' && node.terminal_action.fallback_calendar_config && (
              <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <Calendar className="w-2.5 h-2.5 flex-shrink-0" />
                <span className="truncate">
                  Fallback: {formatCalendarOptionLabel({
                    providerName: node.terminal_action.fallback_calendar_config.name,
                    details: node.terminal_action.fallback_calendar_config.details,
                  })}
                </span>
              </div>
            )}
            {node.terminal_action?.type === 'escalation' && (
              <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <User className="w-2.5 h-2.5 flex-shrink-0" />
                <span className="truncate">
                  {node.terminal_action.value ? node.terminal_action.value : 'Any available'}
                </span>
              </div>
            )}
            {/* Edit icon on hover */}
            <div className="absolute -top-2 -right-2 hidden group-hover/action:flex w-5 h-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
              <Pencil className="w-2.5 h-2.5" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Sample Preview DAG for Upgrade Overlay
// ============================================

type PreviewNodeData = {
  label: string;
  isRoot?: boolean;
  icon?: 'crown' | 'user' | 'database' | 'route';
  fields?: number;
};

type PreviewEdgeData = {
  classification?: 'ai' | 'rules';
};

type PreviewDiagramNode = Node<PreviewNodeData, 'previewNode'>;
type PreviewDiagramEdge = Edge<PreviewEdgeData, 'previewEdge'>;

function PreviewNode({ data }: NodeProps<PreviewDiagramNode>) {
  const { label, isRoot, icon, fields } = data;
  
  const IconComponent = icon === 'crown' ? Crown : icon === 'user' ? User : icon === 'database' ? Database : Route;
  
  return (
    <div
      className={clsx(
        'w-[130px] h-[100px] rounded-lg border bg-card shadow-sm flex flex-col items-center justify-center p-2',
        isRoot
          ? 'border-primary/30 bg-primary/[0.02]'
          : 'border-border'
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-primary/60 !w-2 !h-2 !-top-1" />
      
      <div className={clsx(
        'w-7 h-7 rounded-lg flex items-center justify-center mb-1',
        isRoot ? 'bg-primary/20' : 'bg-muted'
      )}>
        <IconComponent className={clsx('w-4 h-4', isRoot ? 'text-primary' : 'text-muted-foreground')} />
      </div>
      
      <div className="text-[10px] font-medium text-center leading-tight mb-1">{label}</div>
      
      {fields !== undefined && fields > 0 && (
        <Badge variant="secondary" className="text-[8px] px-1 py-0">
          {fields} fields
        </Badge>
      )}
      
      <Handle type="source" position={Position.Bottom} className="!bg-primary/60 !w-2 !h-2 !-bottom-1" />
    </div>
  );
}

function PreviewEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}: EdgeProps<PreviewDiagramEdge>) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const classification = data?.classification;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd as string | undefined} style={style as React.CSSProperties | undefined} />
      {classification && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'none',
            }}
          >
            {classification === 'ai' ? (
              <Badge 
                variant="secondary" 
                className="gap-0.5 bg-purple-100 text-purple-700 border border-purple-300 shadow-sm dark:bg-purple-950 dark:text-purple-300 dark:border-purple-700 text-[8px] px-1 py-0"
              >
                <Sparkles className="h-2 w-2" />
                AI
              </Badge>
            ) : (
              <Badge 
                variant="secondary" 
                className="gap-0.5 bg-emerald-100 text-emerald-700 border border-emerald-300 shadow-sm dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700 text-[8px] px-1 py-0"
              >
                <Scale className="h-2 w-2" />
                Rules
              </Badge>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const previewNodeTypes = { previewNode: PreviewNode };
const previewEdgeTypes = { previewEdge: PreviewEdge };

// Sample nodes showing a realistic visitor workflow
const samplePreviewNodes: PreviewDiagramNode[] = [
  // Root
  { id: 'root', type: 'previewNode', position: { x: 250, y: 0 }, data: { label: 'All Visitors', isRoot: true, icon: 'crown' } },
  // First level - main segments
  { id: 'enterprise', type: 'previewNode', position: { x: 50, y: 140 }, data: { label: 'Enterprise', icon: 'database', fields: 5 } },
  { id: 'smb', type: 'previewNode', position: { x: 250, y: 140 }, data: { label: 'SMB', icon: 'user', fields: 3 } },
  { id: 'startup', type: 'previewNode', position: { x: 450, y: 140 }, data: { label: 'Startup', icon: 'route', fields: 2 } },
  // Second level - sub-segments
  { id: 'ent-sales', type: 'previewNode', position: { x: 0, y: 280 }, data: { label: 'Sales Ready', icon: 'user', fields: 2 } },
  { id: 'ent-eval', type: 'previewNode', position: { x: 130, y: 280 }, data: { label: 'Evaluating', icon: 'database', fields: 1 } },
  { id: 'smb-qualified', type: 'previewNode', position: { x: 280, y: 280 }, data: { label: 'Qualified', icon: 'user', fields: 2 } },
  { id: 'startup-demo', type: 'previewNode', position: { x: 430, y: 280 }, data: { label: 'Demo Request', icon: 'route', fields: 1 } },
];

const samplePreviewEdges: PreviewDiagramEdge[] = [
  { id: 'e1', source: 'root', target: 'enterprise', type: 'previewEdge', data: { classification: 'ai' } },
  { id: 'e2', source: 'root', target: 'smb', type: 'previewEdge', data: { classification: 'ai' } },
  { id: 'e3', source: 'root', target: 'startup', type: 'previewEdge', data: { classification: 'rules' } },
  { id: 'e4', source: 'enterprise', target: 'ent-sales', type: 'previewEdge', data: { classification: 'rules' } },
  { id: 'e5', source: 'enterprise', target: 'ent-eval', type: 'previewEdge', data: { classification: 'ai' } },
  { id: 'e6', source: 'smb', target: 'smb-qualified', type: 'previewEdge', data: { classification: 'rules' } },
  { id: 'e7', source: 'startup', target: 'startup-demo', type: 'previewEdge', data: { classification: 'ai' } },
];

// ============================================

function parseTrackNumber(systemName: string): number | null {
  // Accept legacy numeric IDs (e.g. "3") and new prefixed IDs (e.g. "track_3")
  if (/^\d+$/.test(systemName)) {
    const n = parseInt(systemName, 10);
    return Number.isFinite(n) ? n : null;
  }
  const match = /^track_(\d+)$/.exec(systemName);
  if (!match) return null;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) ? n : null;
}

function isTrackSystemName(systemName: string): boolean {
  return /^track_\d+$/.test(systemName);
}

function getNextTrackSystemName(existing: Set<string>): string {
  let max = 0;
  for (const name of existing) {
    const n = parseTrackNumber(name);
    if (n == null) continue;
    max = Math.max(max, n);
  }
  return `track_${max + 1}`;
}

function createRootTrack(existingSystemNames: Set<string>): VisitorTrackNode {
  const systemName = getNextTrackSystemName(existingSystemNames);
  return {
    id: Date.now().toString(),
    name: 'Visitor Type',
    system_name: systemName,
    parent_system_name: null,
    sort_order: 0,
    classification_instructions: '',
    conversation_goals: '',
    fields_to_collect: [],
    labels_to_collect: [],
  };
}

export default function TrackBuilder({
  agentId,
  agentGoal,
  agentPersona,
  trackNodes,
  setTrackNodes,
  enableTracks,
  setEnableTracks,
  setNeedsUpdate,
  form,
  actions,
}: TrackBuilderProps) {
  const { checkLicenseType } = useUser();
  const isBusiness = checkLicenseType('business');
  const isActivate = checkLicenseType('activate');
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [isDiagramFullscreen, setIsDiagramFullscreen] = useState(false);
  const reactFlowInstanceRef = useRef<any>(null);

  // Close fullscreen diagram with Escape
  useEffect(() => {
    if (!isDiagramFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDiagramFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDiagramFullscreen]);

  // When entering fullscreen, fit the graph to the new viewport
  useEffect(() => {
    if (!isDiagramFullscreen) return;
    const raf = requestAnimationFrame(() => {
      reactFlowInstanceRef.current?.fitView?.({ padding: 0.2 });
    });
    return () => cancelAnimationFrame(raf);
  }, [isDiagramFullscreen]);

  const taxonomyFields: TaxonomyField[] = useMemo(() => {
    const arr = Array.isArray(form) ? form : [];
    return arr
      .filter((f) => f && typeof f === 'object' && typeof f.name === 'string' && f.name.trim())
      .map((f) => ({
        name: String(f.name),
        label: String(f.label || f.name),
        required: !!f.required,
        question: !!f.question,
        ai_label: !!f.ai_label,
        description: f.description ? String(f.description) : undefined,
        type: f.type ? String(f.type) : undefined,
        options: Array.isArray(f.options) ? f.options : undefined,
        hidden: !!f.hidden,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [form]);

  const workflowConditionFields = useMemo(
    () => buildWorkflowConditionFields(taxonomyFields),
    [taxonomyFields]
  );

  // Separate regular fields/questions from AI labels for track editor
  const regularTaxonomyFields = useMemo(() => taxonomyFields.filter(f => !f.ai_label), [taxonomyFields]);
  const aiLabelTaxonomyFields = useMemo(() => taxonomyFields.filter(f => f.ai_label), [taxonomyFields]);

  const taxonomyFieldNameSet = useMemo(() => {
    return new Set((taxonomyFields || []).map((f) => f.name));
  }, [taxonomyFields]);

  const existingSystemNames = useMemo(() => {
    return new Set((trackNodes || []).map((n) => n.system_name));
  }, [trackNodes]);

  // Migrate legacy system_name values to prefixed IDs: track_<number>
  useEffect(() => {
    const nodes = trackNodes || [];
    if (nodes.length === 0) return;
    const hasNonPrefixed = nodes.some((n) => !isTrackSystemName(String(n.system_name)));
    if (!hasNonPrefixed) return;

    // Assign track_<n> IDs deterministically by parent/sort_order so mapping is stable.
    const childrenMap = new Map<string | null, VisitorTrackNode[]>();
    for (const n of nodes) {
      const parent = n.parent_system_name ?? null;
      const list = childrenMap.get(parent) || [];
      list.push(n);
      childrenMap.set(parent, list);
    }
    for (const list of childrenMap.values()) {
      list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }

    const mapping = new Map<string, string>(); // old -> new system_name
    const usedNewIds = new Set<string>();

    // First, reserve/keep any already-prefixed IDs to avoid churn.
    for (const n of nodes) {
      const oldKey = String(n.system_name);
      if (isTrackSystemName(oldKey)) {
        mapping.set(oldKey, oldKey);
        usedNewIds.add(oldKey);
      }
    }

    // Then, try to preserve legacy numeric IDs as track_<same number> where possible.
    for (const n of nodes) {
      const oldKey = String(n.system_name);
      if (mapping.has(oldKey)) continue;
      const num = /^\d+$/.test(oldKey) ? parseInt(oldKey, 10) : null;
      if (num == null || !Number.isFinite(num)) continue;
      const candidate = `track_${num}`;
      if (!usedNewIds.has(candidate)) {
        mapping.set(oldKey, candidate);
        usedNewIds.add(candidate);
      }
    }

    let next = 1;
    const allocNext = (): string => {
      while (usedNewIds.has(`track_${next}`)) next += 1;
      const id = `track_${next}`;
      usedNewIds.add(id);
      next += 1;
      return id;
    };

    const alloc = (node: VisitorTrackNode) => {
      const oldKey = String(node.system_name);
      if (mapping.has(oldKey)) return;
      mapping.set(oldKey, allocNext());
      const kids = childrenMap.get(node.system_name) || [];
      for (const child of kids) alloc(child);
    };

    const roots = childrenMap.get(null) || [];
    for (const r of roots) alloc(r);
    // Allocate any remaining (orphans/cycles)
    for (const n of nodes) alloc(n);

    const migrated = nodes.map((n) => {
      const oldKey = String(n.system_name);
      const newSystem = mapping.get(oldKey) || oldKey;
      const oldParent = n.parent_system_name;
      const newParent = oldParent != null ? (mapping.get(String(oldParent)) || null) : null;
      return {
        ...n,
        system_name: newSystem,
        parent_system_name: newParent,
      };
    });

    setTrackNodes(migrated);
    // Don't auto-save on migration; next explicit edit will persist.
  }, [trackNodes, setTrackNodes]);

  const nodeBySystemName = useMemo(() => {
    const map = new Map<string, VisitorTrackNode>();
    for (const n of trackNodes || []) map.set(n.system_name, n);
    return map;
  }, [trackNodes]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, VisitorTrackNode[]>();
    for (const node of trackNodes || []) {
      const parent = node.parent_system_name ?? null;
      const list = map.get(parent) || [];
      list.push(node);
      map.set(parent, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }
    return map;
  }, [trackNodes]);

  const duplicateSystemNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of trackNodes || []) {
      counts.set(n.system_name, (counts.get(n.system_name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
  }, [trackNodes]);

  const roots = childrenByParent.get(null) || [];
  const root = roots[0];
  const rootChildren = root ? (childrenByParent.get(root.system_name) || []) : [];
  const canEnableTracks = !!root && rootChildren.length >= 2;
  const rootCaption = (root?.name || '').trim() || 'the top-level track';

  // System/out-of-the-box field names that come by default
  const systemFieldNames = ['email', 'first_name', 'last_name', 'phone', 'company', 'job_title'];

  // Checklist item 1: Data capture configured - has custom fields beyond system fields
  const hasDataCaptureConfigured = useMemo(() => {
    const formArr = Array.isArray(form) ? form : [];
    return formArr.some((f) => f && typeof f === 'object' && typeof f.name === 'string' && !systemFieldNames.includes(f.name));
  }, [form]);

  // Checklist item 2: Routing action configured - at least one leaf node has terminal_action
  const hasRoutingActionConfigured = useMemo(() => {
    const nodes = trackNodes || [];
    if (nodes.length === 0) return false;
    
    // Find all leaf nodes (nodes with no children)
    const leafNodes = nodes.filter((node) => {
      const children = childrenByParent.get(node.system_name) || [];
      return children.length === 0 && node.parent_system_name !== null; // Exclude root
    });
    
    // Check if at least one leaf has a terminal action configured.
    // Escalation can be configured as "any available" (empty value).
    // Start onboarding uses the URL from agent config, so no value needed.
    return leafNodes.some((leaf) => {
      const action = leaf.terminal_action;
      if (!action || !action.type) return false;
      if (action.type === 'escalation' || action.type === 'start_onboarding') return true;
      return Boolean(action.value);
    });
  }, [trackNodes, childrenByParent]);

  const reachableSystemNames = useMemo(() => {
    const visited = new Set<string>();
    const stack = [...roots];
    while (stack.length) {
      const node = stack.pop();
      if (!node) continue;
      if (visited.has(node.system_name)) continue;
      visited.add(node.system_name);
      const children = childrenByParent.get(node.system_name) || [];
      for (const child of children) stack.push(child);
    }
    return visited;
  }, [childrenByParent, roots]);

  const orphanNodes = useMemo(() => {
    return (trackNodes || []).filter((n) => !reachableSystemNames.has(n.system_name));
  }, [trackNodes, reachableSystemNames]);

  const getEffectiveFieldCount = (node: VisitorTrackNode): number => {
    const fieldSet = new Set<string>();
    const visitedNodes = new Set<string>();
    let cur: VisitorTrackNode | undefined = node;
    while (cur && !visitedNodes.has(cur.system_name)) {
      visitedNodes.add(cur.system_name);
      // Root track fields are intentionally ignored (configure fields on child tracks).
      if (cur.parent_system_name !== null) {
        for (const f of cur.fields_to_collect || []) fieldSet.add(f);
      }
      const parentName = cur.parent_system_name;
      if (!parentName) break;
      cur = nodeBySystemName.get(parentName);
    }
    return fieldSet.size;
  };

  const siblingMeta = useMemo(() => {
    const meta = new Map<string, { index: number; count: number }>();
    for (const [, siblings] of childrenByParent.entries()) {
      siblings.forEach((child, idx) => {
        meta.set(child.system_name, { index: idx, count: siblings.length });
      });
    }
    return meta;
  }, [childrenByParent]);

  // Ensure there's always a single top-level track to anchor the taxonomy.
  // We default this to "Visitor Type" so it's clear what the classification goal is.
  useEffect(() => {
    if (!isBusiness) return;
    const hasRoot = (trackNodes || []).some((n) => n.parent_system_name === null);
    if (!hasRoot) {
      const root = createRootTrack(existingSystemNames);
      setTrackNodes([root, ...(trackNodes || [])]);
    }
  }, [isBusiness, trackNodes, existingSystemNames, setTrackNodes]);

  const { get, post } = useApi();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDefaultTab, setModalDefaultTab] = useState<'entry' | 'conversation' | 'action'>('entry');
  const [isAiBuildModalOpen, setIsAiBuildModalOpen] = useState(false);
  const [aiBuildPrompt, setAiBuildPrompt] = useState('');
  const [aiBuildError, setAiBuildError] = useState<string | null>(null);
  const [isAiBuilding, setIsAiBuilding] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [modalParentSystemName, setModalParentSystemName] = useState<string | null>(null);
  const [editingNodeSystemName, setEditingNodeSystemName] = useState<string | null>(null);

  // Basic info
  const [nodeName, setNodeName] = useState('');
  
  // Entry Criteria tab
  const [nodeClassificationInstructions, setNodeClassificationInstructions] = useState('');
  const [nodeConditions, setNodeConditions] = useState<RoutingCondition[]>([]);
  const [nodeFormula, setNodeFormula] = useState('');
  
  // Conversation tab
  const [nodeConversationGoals, setNodeConversationGoals] = useState('');
  const [nodeFields, setNodeFields] = useState<string[]>([]);
  const [nodeLabels, setNodeLabels] = useState<string[]>([]);

  const missingNodeFields = useMemo(() => {
    const current = Array.isArray(nodeFields) ? nodeFields : [];
    return current.filter((name) => typeof name === 'string' && name.trim() && !taxonomyFieldNameSet.has(name));
  }, [nodeFields, taxonomyFieldNameSet]);
  
  // Action tab (leaf nodes only)
  const [nodeActionType, setNodeActionType] = useState<'link' | 'calendar' | 'navigate' | 'escalation' | 'start_onboarding' | ''>('');
  const [nodeActionValue, setNodeActionValue] = useState('');
  const [nodeFallbackCalendarValue, setNodeFallbackCalendarValue] = useState('');
  const [nodeActionPrompt, setNodeActionPrompt] = useState('');
  const [calendarIntegrations, setCalendarIntegrations] = useState<any[]>([]);
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const ESCALATION_ANY_AVAILABLE_VALUE = '__any_available__';

  // Legacy alias for backward compatibility with existing code
  const nodeInstructions = nodeClassificationInstructions;
  const setNodeInstructions = setNodeClassificationInstructions;

  const computedSystemNameForCreate = useMemo(() => {
    return getNextTrackSystemName(existingSystemNames);
  }, [existingSystemNames]);

  const openCreate = (parentSystemName: string | null) => {
    setModalMode('create');
    setModalDefaultTab('entry');
    setModalParentSystemName(parentSystemName);
    setEditingNodeSystemName(null);
    setNodeName('');
    setNodeClassificationInstructions('');
    setNodeConditions([]);
    setNodeFormula('');
    setNodeConversationGoals('');
    setNodeFields([]);
    setNodeLabels([]);
    setNodeActionType('');
    setNodeActionValue('');
    setNodeFallbackCalendarValue('');
    setNodeActionPrompt('');
    setIsModalOpen(true);
  };

  const openEdit = (node: VisitorTrackNode, tab?: 'entry' | 'conversation' | 'action') => {
    setModalMode('edit');
    setModalDefaultTab(tab || 'entry');
    setModalParentSystemName(node.parent_system_name ?? null);
    setEditingNodeSystemName(node.system_name);
    setNodeName(node.name || '');
    setNodeClassificationInstructions(node.classification_instructions || '');
    setNodeConditions(node.classification_rules?.conditions || []);
    setNodeFormula(node.classification_rules?.formula || '');
    setNodeConversationGoals(node.conversation_goals || '');
    setNodeFields(Array.isArray(node.fields_to_collect) ? node.fields_to_collect : []);
    setNodeLabels(Array.isArray(node.labels_to_collect) ? node.labels_to_collect : []);
    const actionType = node.terminal_action?.type || '';
    setNodeActionType(actionType);
    if (actionType === 'escalation') {
      const existingValue = (node.terminal_action?.value || '').trim();
      setNodeActionValue(existingValue ? existingValue : ESCALATION_ANY_AVAILABLE_VALUE);
      const existingFallbackCalendarId = (node.terminal_action?.fallback_calendar_config?.id || '').trim();
      setNodeFallbackCalendarValue(existingFallbackCalendarId);
    } else {
      setNodeActionValue(node.terminal_action?.value || '');
      setNodeFallbackCalendarValue('');
    }
    setNodeActionPrompt(node.terminal_action_prompt || '');
    setIsModalOpen(true);
  };

  const openAiBuild = () => {
    setAiBuildPrompt('');
    setAiBuildError(null);
    setIsAiBuilding(false);
    setIsAiBuildModalOpen(true);
  };

  const handleBuildWithAI = async () => {
    if (!agentId) return;
    const prompt = aiBuildPrompt.trim();
    if (!prompt) return;

    setIsAiBuilding(true);
    setAiBuildError(null);

    try {
      const fieldsPayload = (taxonomyFields || [])
        .filter((f) => !f.hidden)
        .map((f) => ({
          name: f.name,
          label: f.label,
          required: f.required,
          question: f.question,
          type: f.type,
          options: f.options,
        }));

      const response = await post(`/agents/${agentId}/generate-tracks`, {
        description: prompt,
        fields: fieldsPayload,
        goal: agentGoal || '',
        persona: agentPersona || '',
      });

      const nodes = response?.data?.nodes;
      if (!Array.isArray(nodes) || nodes.length === 0) {
        throw new Error('Invalid workflow returned');
      }

      setTrackNodes(nodes);
      setNeedsUpdate(true);
      toast.success('Workflow generated. Review and tweak as needed.', toastConfig);
      setIsAiBuildModalOpen(false);
    } catch (error: any) {
      console.error('Error generating tracks:', error);
      setAiBuildError(error.response?.data?.detail || 'Failed to generate workflow');
    } finally {
      setIsAiBuilding(false);
    }
  };

  const toggleField = (fieldName: string, checked: boolean) => {
    setNodeFields((prev) => {
      const set = new Set(prev);
      if (checked) set.add(fieldName);
      else set.delete(fieldName);
      return Array.from(set);
    });
  };

  const toggleLabel = (labelName: string, checked: boolean) => {
    setNodeLabels((prev) => {
      const set = new Set(prev);
      if (checked) set.add(labelName);
      else set.delete(labelName);
      return Array.from(set);
    });
  };

  const upsertNode = () => {
    const trimmedName = (nodeName || '').trim();
    if (!trimmedName) {
      toast.error('Track name is required.', toastConfig);
      return;
    }

    // Validate/normalize terminal action values (leaf nodes only; root cannot persist terminal actions)
    const rawActionValue = (nodeActionValue || '').trim();
    const isStartOnboarding = nodeActionType === 'start_onboarding';
    const shouldPersistTerminalAction = Boolean(nodeActionType && (rawActionValue || isStartOnboarding) && modalParentSystemName !== null);
    let actionValueForSave = rawActionValue;
    const isEscalationAnyAvailable =
      nodeActionType === 'escalation' && rawActionValue === ESCALATION_ANY_AVAILABLE_VALUE;
    if (isEscalationAnyAvailable) {
      // Persist empty value to mean "any available" (backend omits human_rep_email when value is falsy).
      actionValueForSave = '';
    }
    if (shouldPersistTerminalAction && nodeActionType === 'navigate') {
      const isRelativePath = actionValueForSave.startsWith('/');
      const isHttpUrl = /^https?:\/\//i.test(actionValueForSave);
      if (!isRelativePath && !isHttpUrl) {
        toast.error(
          'Please enter a valid path or URL for the navigate action (use /path or https://example.com/path).',
          toastConfig
        );
        return;
      }
      if (isHttpUrl) {
        try {
          const parsed = new URL(actionValueForSave);
          if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            throw new Error('Invalid URL protocol');
          }
        } catch {
          toast.error(
            'Please enter a valid URL for the navigate action (include https://) or use a relative path like /pricing.',
            toastConfig
          );
          return;
        }
      }
    }
    if (shouldPersistTerminalAction && nodeActionType === 'link') {
      // Normalize common inputs like "example.com" -> "https://example.com"
      if (!/^https?:\/\//i.test(actionValueForSave)) {
        actionValueForSave = `https://${actionValueForSave}`;
      }
      try {
        const parsed = new URL(actionValueForSave);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          throw new Error('Invalid URL protocol');
        }
      } catch {
        toast.error('Please enter a valid URL for the link action (include https://).', toastConfig);
        return;
      }
    }

    const trimmedFormula = (nodeFormula || '').trim();
    if (nodeConditions.length > 0) {
      if (!trimmedFormula) {
        toast.error('Logical formula is required when classification conditions are added.', toastConfig);
        return;
      }

      const validLetters = nodeConditions.map((condition) => condition.letter);
      const formulaValidation = validateFormula(trimmedFormula, validLetters);
      if (!formulaValidation.isValid) {
        toast.error(formulaValidation.errors.join('; '), toastConfig);
        return;
      }
    }

    // Build classification rules if conditions exist
    const classificationRules: TrackClassificationRules | undefined = 
      nodeConditions.length > 0
        ? { conditions: nodeConditions, formula: trimmedFormula }
        : undefined;

    // Build terminal action if specified (only for non-root nodes)
    let terminalAction: TrackTerminalAction | undefined = undefined;
    if (
      nodeActionType &&
      modalParentSystemName !== null &&
      (actionValueForSave || isEscalationAnyAvailable || isStartOnboarding)
    ) {
      terminalAction = { 
        type: nodeActionType as TrackTerminalAction['type'], 
        value: actionValueForSave
      };
      
      // If calendar action, include the full calendar config with details
      if (nodeActionType === 'calendar') {
        if (selectedCalendarIntegration) {
          terminalAction.calendar_config = {
            id: selectedCalendarIntegration.id,
            name: selectedCalendarIntegration.name,
            details: selectedCalendarIntegration.details || {},
          };
        } else if (modalMode === 'edit' && editingNodeSystemName) {
          // Fallback: preserve existing calendar_config if integration lookup fails
          const existingNode = (trackNodes || []).find((n) => n.system_name === editingNodeSystemName);
          if (existingNode?.terminal_action?.calendar_config) {
            terminalAction.calendar_config = existingNode.terminal_action.calendar_config;
          }
        }
      } else if (nodeActionType === 'escalation' && nodeFallbackCalendarValue) {
        if (selectedFallbackCalendarIntegration) {
          terminalAction.fallback_calendar_config = {
            id: selectedFallbackCalendarIntegration.id,
            name: selectedFallbackCalendarIntegration.name,
            details: selectedFallbackCalendarIntegration.details || {},
          };
        } else if (modalMode === 'edit' && editingNodeSystemName) {
          // Fallback: preserve existing fallback_calendar_config if integration lookup fails
          const existingNode = (trackNodes || []).find((n) => n.system_name === editingNodeSystemName);
          const existingFallback = existingNode?.terminal_action?.fallback_calendar_config;
          if (existingFallback && existingFallback.id === nodeFallbackCalendarValue) {
            terminalAction.fallback_calendar_config = existingFallback;
          }
        }
      }
    }

    if (modalMode === 'create') {
      const systemName = computedSystemNameForCreate;
      const siblings = (trackNodes || []).filter(
        (n) => (n.parent_system_name ?? null) === (modalParentSystemName ?? null)
      );
      const maxOrder = siblings.reduce((max, n) => Math.max(max, n.sort_order ?? 0), 0);
      const isCreatingRoot = modalParentSystemName === null;
      const newNode: VisitorTrackNode = {
        id: Date.now().toString(),
        name: trimmedName,
        system_name: systemName,
        parent_system_name: modalParentSystemName ?? null,
        sort_order: siblings.length === 0 ? 0 : maxOrder + 1,
        classification_instructions: isCreatingRoot ? '' : (nodeClassificationInstructions || ''),
        classification_rules: isCreatingRoot ? undefined : classificationRules,
        conversation_goals: nodeConversationGoals || '',
        fields_to_collect: nodeFields,
        labels_to_collect: nodeLabels,
        terminal_action: isCreatingRoot ? undefined : terminalAction,
        terminal_action_prompt: nodeActionPrompt || '',
      };

      const newNodes = [...(trackNodes || []), newNode];

      setTrackNodes(newNodes);
      // Persist track configuration to the draft agent even if tracks are not enabled yet.
      setNeedsUpdate(true);
      setIsModalOpen(false);
      return;
    }

    // edit
    if (!editingNodeSystemName) return;
    const currentNode = (trackNodes || []).find((n) => n.system_name === editingNodeSystemName);
    const isRoot = currentNode?.parent_system_name === null;
    const updated = (trackNodes || []).map((n) => {
      if (n.system_name !== editingNodeSystemName) return n;
      return {
        ...n,
        name: trimmedName,
        classification_instructions: isRoot ? '' : (nodeClassificationInstructions || ''),
        classification_rules: isRoot ? undefined : classificationRules,
        conversation_goals: nodeConversationGoals || '',
        fields_to_collect: nodeFields,
        labels_to_collect: nodeLabels,
        terminal_action: isRoot ? undefined : terminalAction,
        terminal_action_prompt: nodeActionPrompt || '',
      };
    });

    setTrackNodes(updated);
    // Persist track configuration to the draft agent even if tracks are not enabled yet.
    setNeedsUpdate(true);
    setIsModalOpen(false);
  };

  const deleteNode = (node: VisitorTrackNode) => {
    if (!isBusiness) return;
    if (node.parent_system_name === null) {
      toast.error('Root track cannot be deleted.', toastConfig);
      return;
    }
    const hasChildren = (trackNodes || []).some((n) => n.parent_system_name === node.system_name);
    if (hasChildren) {
      toast.error('You must delete child tracks first.', toastConfig);
      return;
    }
    if (!confirm(`Delete track "${node.name}"?`)) return;

    setTrackNodes((trackNodes || []).filter((n) => n.system_name !== node.system_name));
    // Persist track configuration to the draft agent even if tracks are not enabled yet.
    setNeedsUpdate(true);
  };

  const moveNode = (node: VisitorTrackNode, direction: 'up' | 'down') => {
    if (!isBusiness) return;
    const parent = node.parent_system_name ?? null;
    const siblings = (trackNodes || [])
      .filter((n) => (n.parent_system_name ?? null) === parent)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    const idx = siblings.findIndex((n) => n.system_name === node.system_name);
    if (idx === -1) return;
    const swapWithIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWithIdx < 0 || swapWithIdx >= siblings.length) return;

    const a = siblings[idx];
    const b = siblings[swapWithIdx];
    const aOrder = a.sort_order ?? 0;
    const bOrder = b.sort_order ?? 0;

    const updated = (trackNodes || []).map((n) => {
      if (n.system_name === a.system_name) return { ...n, sort_order: bOrder };
      if (n.system_name === b.system_name) return { ...n, sort_order: aOrder };
      return n;
    });

    setTrackNodes(updated);
    // Persist track configuration to the draft agent even if tracks are not enabled yet.
    setNeedsUpdate(true);
  };

  const attachOrphansToRoot = () => {
    if (!isBusiness) return;
    const root = roots[0];
    if (!root) return;
    if (!confirm(`Attach ${orphanNodes.length} orphan track(s) under the root?`)) return;

    const siblingsUnderRoot = (trackNodes || []).filter(
      (n) => (n.parent_system_name ?? null) === root.system_name
    );
    let nextOrder = siblingsUnderRoot.reduce((max, n) => Math.max(max, n.sort_order ?? 0), 0) + 1;

    const updated = (trackNodes || []).map((n) => {
      if (reachableSystemNames.has(n.system_name)) return n;
      const patched = { ...n, parent_system_name: root.system_name, sort_order: nextOrder };
      nextOrder += 1;
      return patched;
    });
    setTrackNodes(updated);
    // Persist track configuration to the draft agent even if tracks are not enabled yet.
    setNeedsUpdate(true);
  };

  const editingNode = editingNodeSystemName ? nodeBySystemName.get(editingNodeSystemName) : undefined;
  const directChildren = editingNodeSystemName ? (childrenByParent.get(editingNodeSystemName) || []) : [];
  const modalSystemName = modalMode === 'create' ? computedSystemNameForCreate : editingNode?.system_name || '';
  
  // Check if the node being edited should show the Action tab.
  // True for leaf nodes (no children) AND for nodes whose only children are
  // auto-created onboarding nodes (since those are managed automatically via
  // the start_onboarding transition action).
  const isLeafNode = useMemo(() => {
    if (modalMode === 'create') {
      // New nodes start as leaf nodes
      return true;
    }
    if (!editingNodeSystemName) return false;
    const children = childrenByParent.get(editingNodeSystemName) || [];
    if (children.length === 0) return true;
    // Treat the node as "leaf-like" if every child is the locked onboarding node
    return children.every((c) => c.is_onboarding_node);
  }, [modalMode, editingNodeSystemName, childrenByParent]);

  // Compute fields already collected by ancestor nodes
  // Maps field name -> track name that collects it (closest ancestor)
  const ancestorCollectedFields = useMemo(() => {
    const fieldToTrackName = new Map<string, string>();
    
    // Determine the parent system name based on modal mode
    let parentSystemName: string | null = null;
    if (modalMode === 'create') {
      parentSystemName = modalParentSystemName;
    } else if (editingNode) {
      parentSystemName = editingNode.parent_system_name ?? null;
    }
    
    // Walk up the tree collecting fields from all ancestors
    // We go from closest to furthest, so first occurrence wins
    let currentParent = parentSystemName;
    while (currentParent) {
      const parentNode = nodeBySystemName.get(currentParent);
      if (!parentNode) break;
      
      const trackName = parentNode.name || parentNode.system_name;
      
      // Add fields from this ancestor (only if not already set by a closer ancestor)
      for (const field of parentNode.fields_to_collect || []) {
        if (!fieldToTrackName.has(field)) {
          fieldToTrackName.set(field, trackName);
        }
      }

      // Move to the next ancestor
      currentParent = parentNode.parent_system_name ?? null;
    }

    return fieldToTrackName;
  }, [modalMode, modalParentSystemName, editingNode, nodeBySystemName]);

  // Compute AI labels already required by ancestor nodes
  const ancestorCollectedLabels = useMemo(() => {
    const labelToTrackName = new Map<string, string>();

    let parentSystemName: string | null = null;
    if (modalMode === 'create') {
      parentSystemName = modalParentSystemName;
    } else if (editingNode) {
      parentSystemName = editingNode.parent_system_name ?? null;
    }

    let currentParent = parentSystemName;
    while (currentParent) {
      const parentNode = nodeBySystemName.get(currentParent);
      if (!parentNode) break;

      const trackName = parentNode.name || parentNode.system_name;

      for (const label of parentNode.labels_to_collect || []) {
        if (!labelToTrackName.has(label)) {
          labelToTrackName.set(label, trackName);
        }
      }

      currentParent = parentNode.parent_system_name ?? null;
    }

    return labelToTrackName;
  }, [modalMode, modalParentSystemName, editingNode, nodeBySystemName]);

  const selectedCalendarIntegration = useMemo(() => {
    if (nodeActionType !== 'calendar') return undefined;
    return calendarIntegrations.find((int: any) => int.id === nodeActionValue);
  }, [nodeActionType, calendarIntegrations, nodeActionValue]);

  const existingCalendarConfig = useMemo(() => {
    if (modalMode !== 'edit' || !editingNodeSystemName) return undefined;
    return (trackNodes || []).find((n) => n.system_name === editingNodeSystemName)?.terminal_action?.calendar_config;
  }, [modalMode, editingNodeSystemName, trackNodes]);

  const selectedCalendarName = useMemo(() => {
    if (nodeActionType !== 'calendar') return '';
    return selectedCalendarIntegration?.name || existingCalendarConfig?.name || '';
  }, [nodeActionType, selectedCalendarIntegration, existingCalendarConfig]);

  const selectedFallbackCalendarIntegration = useMemo(() => {
    if (nodeActionType !== 'escalation' || !nodeFallbackCalendarValue) return undefined;
    return calendarIntegrations.find((int: any) => int.id === nodeFallbackCalendarValue);
  }, [nodeActionType, nodeFallbackCalendarValue, calendarIntegrations]);

  const existingFallbackCalendarConfig = useMemo(() => {
    if (modalMode !== 'edit' || !editingNodeSystemName) return undefined;
    return (trackNodes || []).find((n) => n.system_name === editingNodeSystemName)?.terminal_action?.fallback_calendar_config;
  }, [modalMode, editingNodeSystemName, trackNodes]);

  const selectedFallbackCalendarName = useMemo(() => {
    if (nodeActionType !== 'escalation' || !nodeFallbackCalendarValue) return '';
    if (selectedFallbackCalendarIntegration?.name) return selectedFallbackCalendarIntegration.name;
    if (existingFallbackCalendarConfig?.id === nodeFallbackCalendarValue) {
      return existingFallbackCalendarConfig?.name || '';
    }
    return '';
  }, [nodeActionType, nodeFallbackCalendarValue, selectedFallbackCalendarIntegration, existingFallbackCalendarConfig]);

  const chiliPiperConfigState = useMemo(() => {
    const schedulingConfig = actions?.scheduling;
    const schedulingFields =
      schedulingConfig && typeof schedulingConfig === 'object' && typeof schedulingConfig.fields === 'object'
        ? schedulingConfig.fields
        : {};
    const { mappedFieldKeys, requiredTrackFields } = getSchedulingMappedFields(schedulingFields);

    const hasValidConfig = Boolean(
      schedulingConfig &&
        schedulingConfig.name === 'chili-piper' &&
        schedulingConfig.details?.domain_name &&
        schedulingConfig.details?.inbound_router &&
        mappedFieldKeys.length > 0
    );

    return { hasValidConfig, mappedFields: requiredTrackFields };
  }, [actions]);

  const effectiveTrackFieldSet = useMemo(() => {
    const fieldSet = new Set<string>([...(nodeFields || []), ...(nodeLabels || [])]);
    ancestorCollectedFields.forEach((_trackName, fieldName) => {
      fieldSet.add(fieldName);
    });
    ancestorCollectedLabels.forEach((_trackName, labelName) => {
      fieldSet.add(labelName);
    });
    return fieldSet;
  }, [nodeFields, nodeLabels, ancestorCollectedFields, ancestorCollectedLabels]);

  const buildChiliPiperSaveState = (isSelected: boolean) => {
    if (!isSelected) {
      return {
        shouldShow: false,
        isBlocked: false,
        configMissing: false,
        missingFields: [] as string[],
      };
    }

    if (!chiliPiperConfigState.hasValidConfig) {
      return {
        shouldShow: true,
        isBlocked: true,
        configMissing: true,
        missingFields: [] as string[],
      };
    }

    const missingMappedFields = chiliPiperConfigState.mappedFields.filter((fieldName) => !effectiveTrackFieldSet.has(fieldName));

    return {
      shouldShow: true,
      isBlocked: missingMappedFields.length > 0,
      configMissing: false,
      missingFields: missingMappedFields,
    };
  };

  const chiliPiperSaveState = buildChiliPiperSaveState(
    nodeActionType === 'calendar' && selectedCalendarName === 'chili-piper'
  );

  const escalationFallbackChiliPiperSaveState = buildChiliPiperSaveState(
    nodeActionType === 'escalation' && selectedFallbackCalendarName === 'chili-piper'
  );

  const defaultConfigState = useMemo(() => {
    const schedulingConfig = actions?.scheduling;
    const schedulingFields =
      schedulingConfig && typeof schedulingConfig === 'object' && typeof schedulingConfig.fields === 'object'
        ? schedulingConfig.fields
        : {};
    const { mappedFieldKeys, requiredTrackFields } = getSchedulingMappedFields(schedulingFields);

    const hasValidConfig = Boolean(
      schedulingConfig &&
        schedulingConfig.name === 'default' &&
        schedulingConfig.details?.form_id &&
        schedulingConfig.details?.team_id &&
        mappedFieldKeys.length > 0
    );

    return { hasValidConfig, mappedFields: requiredTrackFields };
  }, [actions]);

  const buildDefaultSaveState = (isSelected: boolean) => {
    if (!isSelected) {
      return {
        shouldShow: false,
        isBlocked: false,
        configMissing: false,
        missingFields: [] as string[],
      };
    }

    if (!defaultConfigState.hasValidConfig) {
      return {
        shouldShow: true,
        isBlocked: true,
        configMissing: true,
        missingFields: [] as string[],
      };
    }

    const missingMappedFields = defaultConfigState.mappedFields.filter((fieldName) => !effectiveTrackFieldSet.has(fieldName));

    return {
      shouldShow: true,
      isBlocked: missingMappedFields.length > 0,
      configMissing: false,
      missingFields: missingMappedFields,
    };
  };

  const defaultSaveState = buildDefaultSaveState(
    nodeActionType === 'calendar' && selectedCalendarName === 'default'
  );

  const escalationFallbackDefaultSaveState = buildDefaultSaveState(
    nodeActionType === 'escalation' && selectedFallbackCalendarName === 'default'
  );

  const revenueHeroConfigState = useMemo(() => {
    const schedulingConfig = actions?.scheduling;
    const schedulingFields =
      schedulingConfig && typeof schedulingConfig === 'object' && typeof schedulingConfig.fields === 'object'
        ? schedulingConfig.fields
        : {};
    const { mappedFieldKeys, requiredTrackFields } = getSchedulingMappedFields(schedulingFields);

    const hasValidConfig = Boolean(
      schedulingConfig &&
        schedulingConfig.name === 'revenue-hero' &&
        schedulingConfig.details?.router_id &&
        mappedFieldKeys.length > 0
    );

    return { hasValidConfig, mappedFields: requiredTrackFields };
  }, [actions]);

  const buildRevenueHeroSaveState = (isSelected: boolean) => {
    if (!isSelected) {
      return {
        shouldShow: false,
        isBlocked: false,
        configMissing: false,
        missingFields: [] as string[],
      };
    }

    if (!revenueHeroConfigState.hasValidConfig) {
      return {
        shouldShow: true,
        isBlocked: true,
        configMissing: true,
        missingFields: [] as string[],
      };
    }

    const missingMappedFields = revenueHeroConfigState.mappedFields.filter((fieldName) => !effectiveTrackFieldSet.has(fieldName));

    return {
      shouldShow: true,
      isBlocked: missingMappedFields.length > 0,
      configMissing: false,
      missingFields: missingMappedFields,
    };
  };

  const revenueHeroSaveState = buildRevenueHeroSaveState(
    nodeActionType === 'calendar' && selectedCalendarName === 'revenue-hero'
  );

  const escalationFallbackRevenueHeroSaveState = buildRevenueHeroSaveState(
    nodeActionType === 'escalation' && selectedFallbackCalendarName === 'revenue-hero'
  );

  // Fetch calendar integrations when action tab is relevant
  const fetchCalendarIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const response = await get('/integrations/?include_all_calendars=true');
      const calendarApps = ['calendly', 'tidycal', 'cal-com', 'hubspot-meetings', 'chili-piper', 'default', 'revenue-hero'];
      const filtered = response.data.filter((int: any) =>
        calendarApps.includes(int.name) && (int.user_id != null || int.name === 'chili-piper' || int.name === 'default' || int.name === 'revenue-hero')
      );
      setCalendarIntegrations(filtered);
    } catch (error) {
      console.error('Error fetching calendar integrations:', error);
      setCalendarIntegrations([]);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const fetchOrganizationUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await get('/users/');
      setOrganizationUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching organization users:', error);
      setOrganizationUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch data when modal opens and we need action options
  useEffect(() => {
    if (isModalOpen && isLeafNode && modalParentSystemName !== null) {
      if (nodeActionType === 'calendar') {
        fetchCalendarIntegrations();
        fetchOrganizationUsers();
      } else if (nodeActionType === 'escalation') {
        fetchCalendarIntegrations();
        fetchOrganizationUsers();
      }
    }
  }, [isModalOpen, isLeafNode, nodeActionType, modalParentSystemName]);

  const getCalendarOptions = () => {
    return calendarIntegrations.map((integration) => {
      const user = integration.user_id
        ? organizationUsers.find((u: any) => u.id === integration.user_id)
        : null;
      const userName = user
        ? (user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.first_name || user.last_name || user.email)
        : null;
      return {
        id: integration.id,
        name: integration.name,
        displayName: formatCalendarOptionLabel({
          providerName: integration.name,
          userName,
          details: integration.details,
        }),
      };
    });
  };

  const hasCycle = useMemo(() => {
    const nodes = trackNodes || [];
    for (const start of nodes) {
      const seen = new Set<string>();
      let cur: VisitorTrackNode | undefined = start;
      while (cur) {
        if (seen.has(cur.system_name)) return true;
        seen.add(cur.system_name);
        const parent = cur.parent_system_name;
        if (!parent) break;
        cur = nodeBySystemName.get(parent);
      }
    }
    return false;
  }, [trackNodes, nodeBySystemName]);

  const graphLayout = useMemo(() => {
    const nodes = trackNodes || [];
    // ReactFlow node IDs must be unique; system_name should be unique by design.
    const nodeWidth = 240;
    const nodeHeight = 280;

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({
      rankdir: 'TB',
      nodesep: 60,
      ranksep: 180,
      marginx: 30,
      marginy: 30,
    });

    const flowNodes: TrackDiagramNode[] = nodes.map((n) => {
      const meta = siblingMeta.get(n.system_name) || { index: 0, count: 1 };
      const isRoot = n.parent_system_name === null;
      const direct = isRoot ? 0 : (n.fields_to_collect || []).length;
      const effective = getEffectiveFieldCount(n);
      const canMoveUp = meta.index > 0;
      const canMoveDown = meta.index < meta.count - 1;

      return {
        id: n.system_name,
        type: 'trackNode',
        position: { x: 0, y: 0 }, // set by dagre
        data: {
          node: n,
          isBusiness,
          isRoot,
          directFieldCount: direct,
          effectiveFieldCount: effective,
          canMoveUp,
          canMoveDown,
          isOnboardingNode: !!n.is_onboarding_node,
          hasStartOnboardingAction: n.terminal_action?.type === 'start_onboarding',
          formFields: taxonomyFields,
          onAddChild: (parentSystemName: string) => openCreate(parentSystemName),
          onEdit: (node: VisitorTrackNode) => openEdit(node),
          onEditAction: (node: VisitorTrackNode) => openEdit(node, 'action'),
          onDelete: (node: VisitorTrackNode) => deleteNode(node),
          onMove: (node: VisitorTrackNode, direction: 'up' | 'down') => moveNode(node, direction),
        },
      };
    });

    // Build field label map for human-readable edge summaries
    const edgeFieldLabelMap = new Map<string, string>();
    for (const f of workflowConditionFields) {
      edgeFieldLabelMap.set(f.key, f.label || f.key);
      if (f.legacyKey) {
        edgeFieldLabelMap.set(f.legacyKey, f.label || f.legacyKey);
      }
    }

    const nodeBySystemName = new Map(nodes.map((n) => [n.system_name, n]));
    const systemNameSet = new Set(flowNodes.map((n) => n.id));
    const flowEdges: TrackDiagramEdge[] = nodes
      .filter((n) => n.parent_system_name && systemNameSet.has(n.parent_system_name))
      .map((n) => {
        const hasRules =
          !!n.classification_rules?.conditions?.length &&
          Boolean(n.classification_rules?.formula?.trim());
        const hasAi = Boolean(n.classification_instructions?.trim());
        const classification: TrackEdgeClassification = hasRules ? 'rules' : hasAi ? 'ai' : 'none';
        const targetName = (n.name || n.system_name || '').trim() || n.system_name;

        // Build edge label data
        let classificationSummary: string | undefined;
        let ruleCondition: TrackEdgeRuleCondition | undefined;
        let extraConditionCount: number | undefined;

        if (hasRules) {
          const conditions = n.classification_rules!.conditions;
          const first = conditions[0];
          ruleCondition = {
            field: edgeFieldLabelMap.get(first.field) || first.field,
            operator: first.operator,
            value: Array.isArray(first.value) ? first.value.join(', ') : first.value,
          };
          if (conditions.length > 1) {
            extraConditionCount = conditions.length - 1;
          }
        } else if (hasAi) {
          classificationSummary = truncateText(n.classification_instructions, 60);
        }

        return {
          id: `${n.parent_system_name}->${n.system_name}`,
          source: String(n.parent_system_name),
          target: String(n.system_name),
          type: 'classificationEdge',
          data: {
            classification, targetName, classificationSummary, ruleCondition, extraConditionCount,
            onEditTarget: () => {
              const targetNode = nodeBySystemName.get(n.system_name);
              if (targetNode) openEdit(targetNode);
            },
          },
        };
      });

    for (const n of flowNodes) {
      dagreGraph.setNode(n.id, { width: nodeWidth, height: nodeHeight });
    }
    for (const e of flowEdges) {
      dagreGraph.setEdge(e.source, e.target);
    }

    // dagre requires a DAG; if data is cyclic, we skip layout (UI handles cycle display elsewhere).
    if (!hasCycle) {
      try {
        dagre.layout(dagreGraph);
      } catch (error) {
        console.warn('dagre.layout failed; falling back to default node positions.', error);
      }
    }

    const layoutedNodes = flowNodes.map((n) => {
      const pos = dagreGraph.node(n.id);
      if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') return n;
      return {
        ...n,
        position: { x: pos.x - nodeWidth / 2, y: pos.y - nodeHeight / 2 },
      };
    });

    return { nodes: layoutedNodes, edges: flowEdges, nodeWidth, nodeHeight };
  }, [trackNodes, getEffectiveFieldCount, hasCycle, isBusiness, siblingMeta, taxonomyFields, workflowConditionFields]);

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <Upgrade
        showModal={upgradeModalVisible}
        setShowModal={setUpgradeModalVisible}
        sessionId={null}
      />

      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-56">
          <div className="flex items-center gap-2">
            <Label className="block text-base font-semibold leading-6">Track configuration</Label>
            {!isBusiness && (
              <Badge
                variant="secondary"
                className="text-xs"
              >
                <Crown className="w-3 h-3 mr-1" />
                Business
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm font-light text-muted-foreground">
            Define visitor tracks and associate which form fields/questions apply to each track. Fields are inherited from parent tracks.
          </div>
        </div>

        {roots.length > 0 && (
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={
                      isBusiness && agentId && (trackNodes || []).length <= 1
                        ? () => openAiBuild()
                        : undefined
                    }
                    disabled={!isBusiness || !agentId || (trackNodes || []).length > 1}
                  >
                    {isBusiness ? <Sparkles className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
                    Build with AI
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {!isBusiness
                    ? 'Convert plan required'
                    : !agentId
                      ? 'Save the agent before building a workflow with AI'
                      : (trackNodes || []).length > 1
                        ? 'Workflow already configured'
                        : 'Generate an initial workflow from a prompt'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={isBusiness ? () => openCreate(roots[0].system_name) : undefined}
                    disabled={!isBusiness}
                  >
                    {isBusiness ? <Plus className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
                    Add Track
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{isBusiness ? 'Add a child track' : 'Convert plan required'}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {!isBusiness ? (
        <div className="relative overflow-hidden rounded-md border bg-muted/20" style={{ height: 520 }}>
          <ReactFlow
            nodes={samplePreviewNodes}
            edges={samplePreviewEdges}
            nodeTypes={previewNodeTypes}
            edgeTypes={previewEdgeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag={false}
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            preventScrolling={false}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={24} />
          </ReactFlow>

          <div className="absolute inset-0 bg-background/50 backdrop-blur-[3px] flex items-center justify-center">
            <div className="text-center px-6">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background border-2 border-primary/20 flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <div className="text-sm font-medium mb-2">Visitor Workflow</div>
              <p className="text-xs text-muted-foreground mb-4 max-w-sm mx-auto">
                Unlock visitor workflow to tailor data collection and routing. Available on the Convert plan.
              </p>
              <Button
                size="sm"
                onClick={() => setUpgradeModalVisible(true)}
              >
                <Zap className="w-3 h-3 mr-1" />
                Upgrade to Convert
              </Button>
            </div>
          </div>
        </div>
      ) : roots.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          Initializing workflow…
        </div>
      ) : (
        <div className="space-y-3">
          {(duplicateSystemNames.length > 0 || roots.length > 1 || orphanNodes.length > 0) && (
            <div className="space-y-2">
              {duplicateSystemNames.length > 0 && (
                <Alert>
                  <AlertDescription>
                    Duplicate system names detected: <span className="font-mono">{duplicateSystemNames.join(', ')}</span>. This can break the track diagram.
                  </AlertDescription>
                </Alert>
              )}
              {roots.length > 1 && (
                <Alert>
                  <AlertDescription>
                    Multiple top-level tracks detected. Only one top-level track is recommended.
                  </AlertDescription>
                </Alert>
              )}
              {orphanNodes.length > 0 && roots.length > 0 && (
                <Alert>
                  <AlertDescription className="flex items-center justify-between gap-3">
                    <span>
                      {orphanNodes.length} track(s) are not attached to the root (missing/invalid parent) and won’t show in the diagram.
                    </span>
                    {isBusiness && roots.length > 0 && (
                      <Button variant="outline" size="sm" onClick={attachOrphansToRoot}>
                        Attach to root
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {duplicateSystemNames.length > 0 ? (
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              Fix duplicate system names to render the track diagram.
            </div>
          ) : hasCycle ? (
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              Cycle detected in track parent relationships. Please fix the configuration to render the track diagram.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative" style={{ height: 520 }}>
                <div
                  className={clsx(
                    'bg-background overflow-hidden',
                    isDiagramFullscreen
                      ? 'fixed inset-0 z-40 rounded-none border-0'
                      : 'absolute inset-0 rounded-md border'
                  )}
                >
                  {isDiagramFullscreen && (
                    <div className="absolute right-4 top-4 z-10 nodrag nopan">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setIsDiagramFullscreen(false)}
                        aria-label="Exit fullscreen"
                        title="Exit fullscreen"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  <ReactFlow
                    nodes={graphLayout.nodes}
                    edges={graphLayout.edges}
                    nodeTypes={{ trackNode: TrackFlowNode }}
                    edgeTypes={{ classificationEdge: TrackClassificationEdge }}
                    fitView
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={true}
                    proOptions={{ hideAttribution: true }}
                    onInit={(instance) => {
                      reactFlowInstanceRef.current = instance;
                    }}
                  >
                    <Controls
                      onFitView={() => {
                        if (!isDiagramFullscreen) {
                          setIsDiagramFullscreen(true);
                          return;
                        }
                        reactFlowInstanceRef.current?.fitView?.({ padding: 0.2 });
                      }}
                    />
                    <Background gap={24} />
                  </ReactFlow>
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 px-4 py-4 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Workflow Setup Checklist</div>
                    <div className="text-xs text-muted-foreground">
                      Complete these items to fully configure your visitor workflow.
                    </div>
                  </div>
                  {enableTracks ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">
                      Inactive
                    </Badge>
                  )}
                </div>

                <div className="space-y-2">
                  {/* Checklist item: Sub-tracks configured (required for enabling) */}
                  <div className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                    {canEnableTracks ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        Sub-tracks configured
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {canEnableTracks 
                          ? `${rootChildren.length} sub-tracks under ${rootCaption}` 
                          : `Add at least 2 sub-tracks under ${rootCaption}`}
                      </div>
                    </div>
                    <Badge variant={canEnableTracks ? "default" : "secondary"} className={clsx(
                      "text-xs",
                      canEnableTracks && "bg-emerald-600 hover:bg-emerald-700"
                    )}>
                      Required
                    </Badge>
                  </div>

                  {/* Checklist item: Data capture configured */}
                  <div className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                    {hasDataCaptureConfigured ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                    <Database className={clsx(
                      "h-4 w-4 flex-shrink-0",
                      hasDataCaptureConfigured ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        Data capture configured
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {hasDataCaptureConfigured 
                          ? "Custom fields added to capture visitor data" 
                          : "Add custom fields in Data Capture tab beyond the default fields"}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400">
                      Recommended
                    </Badge>
                  </div>

                  {/* Checklist item: Routing action configured */}
                  <div className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                    {hasRoutingActionConfigured ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                    <Route className={clsx(
                      "h-4 w-4 flex-shrink-0",
                      hasRoutingActionConfigured ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">
                        Routing action configured
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {hasRoutingActionConfigured 
                          ? "At least one track endpoint has an action configured" 
                          : "Configure an action (link, calendar, navigate, escalation, or start onboarding) on at least one leaf track"}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400">
                      Recommended
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={isAiBuildModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAiBuildError(null);
            setIsAiBuilding(false);
          }
          setIsAiBuildModalOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Build workflow with AI</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Describe the visitor workflow you want. We’ll generate a root track and sub-tracks you can edit.
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-build-workflow-prompt">Workflow prompt</Label>
              <Textarea
                id="ai-build-workflow-prompt"
                value={aiBuildPrompt}
                onChange={(e) => setAiBuildPrompt(e.target.value)}
                placeholder="Example: Segment visitors into Prospects, Customers, and Partners. Collect company size for prospects, and route customers to support."
                rows={6}
                className="resize-none"
              />
              <div className="text-xs text-muted-foreground">
                AI will only select fields you’ve configured in the Data Capture tab.
              </div>
            </div>

            {aiBuildError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{aiBuildError}</p>
              </div>
            )}

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                This will generate an initial workflow. You can edit names, fields, and classification instructions after.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAiBuildModalOpen(false)} disabled={isAiBuilding}>
              Cancel
            </Button>
            <Button onClick={handleBuildWithAI} disabled={!aiBuildPrompt.trim() || isAiBuilding}>
              {isAiBuilding ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {isAiBuilding ? 'Building…' : 'Build workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modalMode === 'create' ? 'Create Track' : 'Edit Track'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Track Name - Always visible (read-only for locked onboarding nodes) */}
            <div className={clsx("grid grid-cols-1 gap-4", modalMode === 'edit' && "sm:grid-cols-2")}>
              <div className="space-y-2">
                <Label>Track Name</Label>
                <Input
                  value={nodeName}
                  onChange={(e) => setNodeName(e.target.value)}
                  placeholder="e.g. Prospect"
                  readOnly={!!editingNode?.is_onboarding_node}
                  className={editingNode?.is_onboarding_node ? 'opacity-60 cursor-not-allowed' : ''}
                />
                {editingNode?.is_onboarding_node && (
                  <p className="text-xs text-muted-foreground">
                    This is an auto-created onboarding track. Its name is managed automatically.
                  </p>
                )}
              </div>
              {modalMode === 'edit' && (
                <div className="space-y-2">
                  <Label>Track ID</Label>
                  <Input value={modalSystemName} readOnly className="font-mono" />
                  <div className="text-xs text-muted-foreground">
                    Auto-generated ID (e.g. track_1). Stable after creation.
                  </div>
                </div>
              )}
            </div>

            {/* Root node shows goals, fields, and sub-tracks (no entry criteria or terminal actions) */}
            {modalMode === 'edit' && editingNode?.parent_system_name === null ? (
              <div className="space-y-4">
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    The root track is where all visitors start. Set initial goals and fields to collect before classification. Child tracks define the classification criteria.
                  </AlertDescription>
                </Alert>

                {/* Conversation Goals for root */}
                <div className="space-y-2">
                  <Label>Initial Conversation Goals</Label>
                  <Textarea
                    value={nodeConversationGoals}
                    onChange={(e) => setNodeConversationGoals(e.target.value)}
                    placeholder="What should the AI accomplish before classifying the visitor? E.g., 'Greet the visitor warmly, understand their initial needs, and gather basic contact info.'"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Goals the AI should work towards before classifying visitors into child tracks.
                  </p>
                </div>

                {/* Fields to collect for root */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label>Initial Fields to Collect</Label>
                    <div className="text-xs text-muted-foreground">
                      Fields to collect before classification (e.g., name, email).
                    </div>
                  </div>

                  {missingNodeFields.length > 0 ? (
                    <Alert className="bg-amber-500/10 border-amber-500/20">
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <div className="font-medium">Missing fields</div>
                          <div className="text-xs text-muted-foreground">
                            These fields were previously selected but no longer exist in Data Capture. Remove them to stop the AI from asking for them.
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {missingNodeFields.map((name) => (
                              <Badge key={name} variant="secondary" className="gap-1">
                                <span className="font-mono text-xs">{name}</span>
                                <button
                                  type="button"
                                  className="ml-1 rounded hover:bg-muted px-0.5"
                                  onClick={() => setNodeFields((prev) => (prev || []).filter((n) => n !== name))}
                                  aria-label={`Remove missing field ${name}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <div className="max-h-[200px] overflow-y-auto rounded-md border p-3 space-y-2">
                    {regularTaxonomyFields.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No form fields configured yet.</div>
                    ) : (
                      regularTaxonomyFields.map((f) => {
                        const checked = nodeFields.includes(f.name);
                        const checkboxId = `root-track-field-${f.name}`;
                        return (
                          <div key={f.name} className="flex items-start gap-3">
                            <Checkbox
                              id={checkboxId}
                              checked={checked}
                              onCheckedChange={(v) => toggleField(f.name, Boolean(v))}
                            />
                            <label htmlFor={checkboxId} className="flex-1 cursor-pointer">
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="font-medium">{f.label}</div>
                                <span className="font-mono text-xs text-muted-foreground">({f.name})</span>
                                {f.question && <Badge variant="secondary" className="text-xs">Question</Badge>}
                                {f.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                                {f.hidden && <Badge variant="outline" className="text-xs">Hidden</Badge>}
                              </div>
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* AI Labels to collect for root */}
                {aiLabelTaxonomyFields.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label>AI Labels to Fill</Label>
                    <div className="text-xs text-muted-foreground">
                      Labels the AI should fill before classification.
                    </div>
                  </div>

                  <div className="max-h-[200px] overflow-y-auto rounded-md border p-3 space-y-2">
                    {aiLabelTaxonomyFields.map((f) => {
                      const checked = nodeLabels.includes(f.name);
                      const checkboxId = `root-track-label-${f.name}`;
                      return (
                        <div key={f.name} className="flex items-start gap-3">
                          <Checkbox
                            id={checkboxId}
                            checked={checked}
                            onCheckedChange={(v) => toggleLabel(f.name, Boolean(v))}
                          />
                          <label htmlFor={checkboxId} className="flex-1 cursor-pointer">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="font-medium">{f.label}</div>
                              <span className="font-mono text-xs text-muted-foreground">({f.name})</span>
                              <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">AI Label</Badge>
                            </div>
                            {f.description && (
                              <div className="text-xs text-muted-foreground mt-0.5">{f.description}</div>
                            )}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Sub-tracks */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label>Sub-tracks</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={isBusiness ? () => openCreate(editingNode.system_name) : undefined}
                      disabled={!isBusiness}
                    >
                      {isBusiness ? <Plus className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
                      Add sub-track
                    </Button>
                  </div>

                  <div className="rounded-md border p-3 space-y-2 max-h-[200px] overflow-y-auto">
                    {directChildren.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No sub-tracks. Add at least two sub-tracks to enable track classification.</div>
                    ) : (
                      directChildren.map((child) => {
                        const childHasChildren = (childrenByParent.get(child.system_name) || []).length > 0;
                        return (
                          <div key={child.system_name} className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate font-medium">{child.name || child.system_name}</div>
                              <div className="text-xs text-muted-foreground font-mono truncate">
                                {child.system_name}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEdit(child)}
                                disabled={!isBusiness}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteNode(child)}
                                disabled={!isBusiness || childHasChildren}
                                title={childHasChildren ? 'Delete child tracks first' : 'Delete'}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Non-root nodes get tabs */
              <Tabs value={modalDefaultTab} onValueChange={(v) => setModalDefaultTab(v as 'entry' | 'conversation' | 'action')} className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="entry">Classification</TabsTrigger>
                  <TabsTrigger value="conversation">Conversation</TabsTrigger>
                  <TabsTrigger value="action" disabled={!isLeafNode}>
                    Action {!isLeafNode && <span className="text-xs ml-1">(leaf only)</span>}
                  </TabsTrigger>
                </TabsList>

                {/* Entry Criteria Tab */}
                <TabsContent value="entry" className="space-y-4 mt-4">
                  {/* Logical Rules - Primary (evaluated first) */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Classification Rules (Logical)</Label>
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Scale className="h-3 w-3" />
                        Primary
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Deterministic rules evaluated first. If rules match, the visitor is classified immediately without AI.
                    </p>
                    <ConditionBuilder
                      conditions={nodeConditions}
                      setConditions={setNodeConditions}
                      formula={nodeFormula}
                      setFormula={setNodeFormula}
                      conditionFields={workflowConditionFields}
                      showFormula={true}
                    />
                  </div>

                  {/* AI Instructions - Fallback (used when rules don't match) */}
                  <div className={clsx(
                    "space-y-2 pt-4 border-t",
                    nodeConditions.length > 0 && nodeFormula.trim() && "opacity-70"
                  )}>
                    <div className="flex items-center gap-2">
                      <Label>Classification Instructions (AI)</Label>
                      <Badge variant="outline" className="text-xs gap-1">
                        <Sparkles className="h-3 w-3" />
                        Fallback
                      </Badge>
                    </div>
                    {nodeConditions.length > 0 && nodeFormula.trim() ? (
                      <p className="text-xs text-muted-foreground">
                        AI instructions are used as a fallback when logical rules can't be evaluated (e.g., required field data not yet collected).
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        The AI uses these instructions to determine when a visitor should be classified into this track.
                      </p>
                    )}
                    <Textarea
                      value={nodeClassificationInstructions}
                      onChange={(e) => setNodeClassificationInstructions(e.target.value)}
                      placeholder="Describe how the AI should recognize visitors belonging to this track. E.g., 'Classify as Enterprise if they mention large teams, complex requirements, or ask about enterprise features.'"
                      rows={3}
                    />
                  </div>
                </TabsContent>

                {/* Conversation Tab */}
                <TabsContent value="conversation" className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Conversation Goals</Label>
                    <Textarea
                      value={nodeConversationGoals}
                      onChange={(e) => setNodeConversationGoals(e.target.value)}
                      placeholder="What should the AI accomplish while the visitor is in this track? E.g., 'Qualify budget and timeline, identify the decision maker, understand their main pain points.'"
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">
                      Goals the AI should work towards while the visitor is classified in this track.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>Fields to Collect</Label>
                      <div className="text-xs text-muted-foreground">
                        Select which form fields/questions to collect at this track level.
                      </div>
                    </div>

                    {missingNodeFields.length > 0 ? (
                      <Alert className="bg-amber-500/10 border-amber-500/20">
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          <div className="space-y-2">
                            <div className="font-medium">Missing fields</div>
                            <div className="text-xs text-muted-foreground">
                              These fields were previously selected but no longer exist in Data Capture. Remove them to stop the AI from asking for them.
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {missingNodeFields.map((name) => (
                                <Badge key={name} variant="secondary" className="gap-1">
                                  <span className="font-mono text-xs">{name}</span>
                                  <button
                                    type="button"
                                    className="ml-1 rounded hover:bg-muted px-0.5"
                                    onClick={() => setNodeFields((prev) => (prev || []).filter((n) => n !== name))}
                                    aria-label={`Remove missing field ${name}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    <div className="max-h-[260px] overflow-y-auto rounded-md border p-3 space-y-2">
                      {regularTaxonomyFields.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No form fields configured yet.</div>
                      ) : (
                        regularTaxonomyFields.map((f) => {
                          const inheritedFromTrack = ancestorCollectedFields.get(f.name);
                          const isInheritedFromAncestor = !!inheritedFromTrack;
                          const checked = isInheritedFromAncestor || nodeFields.includes(f.name);
                          const checkboxId = `track-field-${f.name}`;
                          return (
                            <div
                              key={f.name}
                              className={`flex items-start gap-3 ${isInheritedFromAncestor ? 'opacity-60' : ''}`}
                            >
                              <Checkbox
                                id={checkboxId}
                                checked={checked}
                                disabled={isInheritedFromAncestor}
                                onCheckedChange={(v) => {
                                  if (!isInheritedFromAncestor) {
                                    toggleField(f.name, Boolean(v));
                                  }
                                }}
                              />
                              <label
                                htmlFor={checkboxId}
                                className={`flex-1 ${isInheritedFromAncestor ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="font-medium">{f.label}</div>
                                  <span className="font-mono text-xs text-muted-foreground">({f.name})</span>
                                  {inheritedFromTrack && (
                                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                      {inheritedFromTrack}
                                    </Badge>
                                  )}
                                  {f.question && <Badge variant="secondary" className="text-xs">Question</Badge>}
                                  {f.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                                  {f.hidden && <Badge variant="outline" className="text-xs">Hidden</Badge>}
                                </div>
                                {f.type ? (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    Type: {f.type}
                                  </div>
                                ) : null}
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* AI Labels to fill at this track */}
                  {aiLabelTaxonomyFields.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <Label>AI Labels to Fill</Label>
                      <div className="text-xs text-muted-foreground">
                        Labels the AI should fill at this track level.
                      </div>
                    </div>

                    <div className="max-h-[200px] overflow-y-auto rounded-md border p-3 space-y-2">
                      {aiLabelTaxonomyFields.map((f) => {
                        const inheritedFromTrack = ancestorCollectedLabels.get(f.name);
                        const isInheritedFromAncestor = !!inheritedFromTrack;
                        const checked = isInheritedFromAncestor || nodeLabels.includes(f.name);
                        const checkboxId = `track-label-${f.name}`;
                        return (
                          <div
                            key={f.name}
                            className={`flex items-start gap-3 ${isInheritedFromAncestor ? 'opacity-60' : ''}`}
                          >
                            <Checkbox
                              id={checkboxId}
                              checked={checked}
                              disabled={isInheritedFromAncestor}
                              onCheckedChange={(v) => {
                                if (!isInheritedFromAncestor) {
                                  toggleLabel(f.name, Boolean(v));
                                }
                              }}
                            />
                            <label
                              htmlFor={checkboxId}
                              className={`flex-1 ${isInheritedFromAncestor ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="font-medium">{f.label}</div>
                                <span className="font-mono text-xs text-muted-foreground">({f.name})</span>
                                {inheritedFromTrack && (
                                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                                    {inheritedFromTrack}
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">AI Label</Badge>
                              </div>
                              {f.description && (
                                <div className="text-xs text-muted-foreground mt-0.5">{f.description}</div>
                              )}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )}

                  {/* Sub-tracks section in edit mode */}
                  {modalMode === 'edit' && editingNode && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <Label>Sub-tracks</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={isBusiness ? () => openCreate(editingNode.system_name) : undefined}
                          disabled={!isBusiness}
                        >
                          {isBusiness ? <Plus className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />}
                          Add sub-track
                        </Button>
                      </div>

                      <div className="rounded-md border p-3 space-y-2 max-h-[180px] overflow-y-auto">
                        {directChildren.length === 0 ? (
                          <div className="text-sm text-muted-foreground">No sub-tracks. This is a leaf node.</div>
                        ) : (
                          directChildren.map((child) => {
                            const childHasChildren = (childrenByParent.get(child.system_name) || []).length > 0;
                            return (
                              <div key={child.system_name} className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="truncate font-medium">{child.name || child.system_name}</div>
                                  <div className="text-xs text-muted-foreground font-mono truncate">
                                    {child.system_name}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEdit(child)}
                                    disabled={!isBusiness}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteNode(child)}
                                    disabled={!isBusiness || childHasChildren}
                                    title={childHasChildren ? 'Delete child tracks first' : 'Delete'}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Action Tab (leaf nodes only) */}
                <TabsContent value="action" className="space-y-4 mt-4">
                  {!isLeafNode ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Terminal actions can only be configured on leaf nodes (tracks with no children). Remove child tracks or configure actions on the leaf nodes instead.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription>
                          Configure what happens when the visitor reaches this track and completes the conversation goals. This is the terminal action for this classification path.
                        </AlertDescription>
                      </Alert>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label>Action Prompt</Label>
                            <Badge variant="outline" className="text-xs">Optional</Badge>
                          </div>
                          <Textarea
                            value={nodeActionPrompt}
                            onChange={(e) => setNodeActionPrompt(e.target.value)}
                            placeholder="Optional: Add any additional instructions for how the AI should behave once this terminal track is reached. This can be used even when no specific action type is selected."
                            rows={3}
                          />
                          <p className="text-xs text-muted-foreground">
                            Free-form instructions the AI should follow after the terminal leaf is reached (in addition to any configured action below).
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Action Type</Label>
                          <Select 
                            value={nodeActionType || 'none'} 
                            onValueChange={(value: 'none' | 'link' | 'calendar' | 'navigate' | 'escalation' | 'start_onboarding') => {
                              setNodeActionType(value === 'none' ? '' : value);
                              setNodeActionValue('');
                              setNodeFallbackCalendarValue('');
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an action (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                <span className="text-muted-foreground">No action</span>
                              </SelectItem>
                              <SelectItem value="link">
                                <div className="flex items-center gap-2">
                                  <LinkIcon className="h-4 w-4" />
                                  <span>Link</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="calendar">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  <span>Calendar Booking</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="navigate">
                                <div className="flex items-center gap-2">
                                  <Route className="h-4 w-4" />
                                  <span>Navigate to page</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="escalation">
                                <div className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  <span>Escalation</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="start_onboarding" disabled={!isActivate}>
                                <div className="flex items-center gap-2">
                                  {isActivate ? <Play className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                                  <span>Start Onboarding</span>
                                  {!isActivate && <span className="text-[10px] text-muted-foreground ml-1">Activate plan</span>}
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {nodeActionType === 'link' && (
                          <div className="space-y-2">
                            <Label>Link URL</Label>
                            <Input
                              value={nodeActionValue}
                              onChange={(e) => setNodeActionValue(e.target.value)}
                              placeholder="https://example.com/page"
                            />
                            <p className="text-xs text-muted-foreground">
                              The AI will direct the visitor to this link when they reach this track.
                            </p>
                          </div>
                        )}

                        {nodeActionType === 'navigate' && (
                          <div className="space-y-2">
                            <Label>Destination</Label>
                            <Input
                              value={nodeActionValue}
                              onChange={(e) => setNodeActionValue(e.target.value)}
                              placeholder="/pricing"
                            />
                            <p className="text-xs text-muted-foreground">
                              The AI will navigate the visitor to this page when they reach this track. Prefer a relative path like <span className="font-mono">/pricing</span>.
                            </p>
                          </div>
                        )}

                        {nodeActionType === 'calendar' && (
                          <div className="space-y-2">
                            <Label>Calendar</Label>
                            {loadingIntegrations ? (
                              <div className="flex items-center justify-center p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                                Loading calendars...
                              </div>
                            ) : getCalendarOptions().length > 0 ? (
                              <Select value={nodeActionValue} onValueChange={setNodeActionValue}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a calendar" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getCalendarOptions().map((cal) => (
                                    <SelectItem key={cal.id} value={cal.id}>
                                      {cal.displayName}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="flex items-center justify-center p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                                No calendars configured. Set up a calendar integration first.
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                              The AI will display this calendar for booking when the visitor reaches this track.
                            </p>
                            {chiliPiperSaveState.shouldShow && chiliPiperSaveState.configMissing && (
                              <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                  Chili Piper is selected, but it is not fully configured in the Actions tab.
                                  Configure Chili Piper router details and field mappings to enable saving.
                                </AlertDescription>
                              </Alert>
                            )}
                            {chiliPiperSaveState.shouldShow &&
                              !chiliPiperSaveState.configMissing &&
                              chiliPiperSaveState.missingFields.length > 0 && (
                                <Alert>
                                  <Info className="h-4 w-4" />
                                  <AlertDescription>
                                    This track path is missing the required contact field: {chiliPiperSaveState.missingFields.join(', ')}.
                                    Add them to this track or a parent track to enable saving.
                                  </AlertDescription>
                                </Alert>
                              )}
                            {defaultSaveState.shouldShow && defaultSaveState.configMissing && (
                              <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                  Default is selected, but it is not fully configured in the Actions tab.
                                  Configure Default Form ID, Team ID, and field mappings to enable saving.
                                </AlertDescription>
                              </Alert>
                            )}
                            {defaultSaveState.shouldShow &&
                              !defaultSaveState.configMissing &&
                              defaultSaveState.missingFields.length > 0 && (
                                <Alert>
                                  <Info className="h-4 w-4" />
                                  <AlertDescription>
                                    This track path is missing the required contact field: {defaultSaveState.missingFields.join(', ')}.
                                    Add them to this track or a parent track to enable saving.
                                  </AlertDescription>
                                </Alert>
                              )}
                            {revenueHeroSaveState.shouldShow && revenueHeroSaveState.configMissing && (
                              <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                  RevenueHero is selected, but it is not fully configured in the Actions tab.
                                  Configure RevenueHero Router ID and field mappings to enable saving.
                                </AlertDescription>
                              </Alert>
                            )}
                            {revenueHeroSaveState.shouldShow &&
                              !revenueHeroSaveState.configMissing &&
                              revenueHeroSaveState.missingFields.length > 0 && (
                                <Alert>
                                  <Info className="h-4 w-4" />
                                  <AlertDescription>
                                    This track path is missing the required contact field: {revenueHeroSaveState.missingFields.join(', ')}.
                                    Add them to this track or a parent track to enable saving.
                                  </AlertDescription>
                                </Alert>
                              )}
                          </div>
                        )}

                        {nodeActionType === 'escalation' && (
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>Escalation target</Label>
                              {loadingUsers ? (
                                <div className="flex items-center justify-center p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                                  Loading team members...
                                </div>
                              ) : (
                                <Select value={nodeActionValue} onValueChange={setNodeActionValue}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a target" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={ESCALATION_ANY_AVAILABLE_VALUE}>
                                      Any available rep
                                    </SelectItem>
                                    {organizationUsers.map((user: any) => (
                                      <SelectItem key={user.id} value={user.email}>
                                        {user.first_name && user.last_name 
                                          ? `${user.first_name} ${user.last_name}` 
                                          : user.email}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <p className="text-xs text-muted-foreground">
                                If you choose “Any available rep”, the AI will call the escalation tool without assigning a specific rep.
                              </p>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label>Fallback calendar</Label>
                                <Badge variant="outline" className="text-xs">Optional</Badge>
                              </div>
                              {loadingIntegrations ? (
                                <div className="flex items-center justify-center p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                                  Loading calendars...
                                </div>
                              ) : getCalendarOptions().length > 0 ? (
                                <Select
                                  value={nodeFallbackCalendarValue || 'none'}
                                  onValueChange={(value) => setNodeFallbackCalendarValue(value === 'none' ? '' : value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="No fallback calendar" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">
                                      <span className="text-muted-foreground">No fallback calendar</span>
                                    </SelectItem>
                                    {getCalendarOptions().map((cal) => (
                                      <SelectItem key={cal.id} value={cal.id}>
                                        {cal.displayName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex items-center justify-center p-3 border rounded-md bg-muted text-sm text-muted-foreground">
                                  No calendars configured. Set up a calendar integration first.
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground">
                                If escalation cannot notify a rep, the AI can show this calendar as a fallback.
                              </p>
                              {escalationFallbackChiliPiperSaveState.shouldShow &&
                                escalationFallbackChiliPiperSaveState.configMissing && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      Fallback Chili Piper is selected, but Chili Piper is not fully configured in the Actions tab.
                                      Configure router details and field mappings to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                              {escalationFallbackChiliPiperSaveState.shouldShow &&
                                !escalationFallbackChiliPiperSaveState.configMissing &&
                                escalationFallbackChiliPiperSaveState.missingFields.length > 0 && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      This track path is missing the required fallback contact field: {escalationFallbackChiliPiperSaveState.missingFields.join(', ')}.
                                      Add them to this track or a parent track to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                              {escalationFallbackDefaultSaveState.shouldShow &&
                                escalationFallbackDefaultSaveState.configMissing && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      Fallback Default is selected, but Default is not fully configured in the Actions tab.
                                      Configure Form ID, Team ID, and field mappings to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                              {escalationFallbackDefaultSaveState.shouldShow &&
                                !escalationFallbackDefaultSaveState.configMissing &&
                                escalationFallbackDefaultSaveState.missingFields.length > 0 && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      This track path is missing the required fallback contact field: {escalationFallbackDefaultSaveState.missingFields.join(', ')}.
                                      Add them to this track or a parent track to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                              {escalationFallbackRevenueHeroSaveState.shouldShow &&
                                escalationFallbackRevenueHeroSaveState.configMissing && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      Fallback RevenueHero is selected, but RevenueHero is not fully configured in the Actions tab.
                                      Configure Router ID and field mappings to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                              {escalationFallbackRevenueHeroSaveState.shouldShow &&
                                !escalationFallbackRevenueHeroSaveState.configMissing &&
                                escalationFallbackRevenueHeroSaveState.missingFields.length > 0 && (
                                  <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertDescription>
                                      This track path is missing the required fallback contact field: {escalationFallbackRevenueHeroSaveState.missingFields.join(', ')}.
                                      Add them to this track or a parent track to enable saving.
                                    </AlertDescription>
                                  </Alert>
                                )}
                            </div>
                          </div>
                        )}

                        {nodeActionType === 'start_onboarding' && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">
                              The AI will navigate the visitor to the onboarding URL configured in the Onboarding tab. Make sure you have set an onboarding URL in the agent's Onboarding settings.
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={upsertNode} disabled={chiliPiperSaveState.isBlocked || escalationFallbackChiliPiperSaveState.isBlocked || defaultSaveState.isBlocked || escalationFallbackDefaultSaveState.isBlocked || revenueHeroSaveState.isBlocked || escalationFallbackRevenueHeroSaveState.isBlocked}>
              {modalMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

